/**
 * Shared layout for the three legal pages (#126, FR-13.1): Terms, Privacy, Refunds.
 *
 * Renders the common chrome — the app header (so "home" works), the binding entity, the
 * policy version + effective date, the support contact, and the site-wide footer —
 * around each page's copy.
 *
 * Deliberately **pure and unauthenticated**: no identity load, no network, deterministic
 * render. That keeps the routes static/prerenderable (crawlable + shareable) so the #103
 * SEO/prerender work can render them unchanged. The product name is read from the
 * {@link BRAND_NAME} config token — never hard-coded (issue #126, decision 2).
 */

import { AppHeader } from '../AppHeader';
import { BRAND_NAME } from '../brand';
import { POLICY_VERSION } from '@jeopardy/shared';
import { useDocumentHead } from '../../seo/useDocumentHead';
import { headModelFor } from '../../seo/route-seo';
import { LEGAL_CONTACT_EMAIL, LEGAL_ENTITY } from './legal';

export interface LegalPageProps {
  /** The document title, e.g. `Terms of Service` (rendered as the page `<h1>`). */
  title: string;
  /** A stable id for e2e/component assertions, e.g. `legal-terms`. */
  testId: string;
  /** The route path (leading `/`) → its shared route-head model (#103/#241, FR-15.3/15.4). */
  path: string;
  /** The document body (already brand-interpolated by the caller). */
  children: React.ReactNode;
}

export function LegalPage({
  title,
  testId,
  path,
  children,
}: LegalPageProps): React.JSX.Element {
  // Per-route SEO for the (SPA) legal routes: title + description + self-canonical, from
  // the shared route-head source so the prerender and client can't drift (FR-15.3/15.4).
  useDocumentHead(headModelFor(path));
  return (
    <div className="app-shell">
      <AppHeader />
      <main className="legal-page" data-testid={testId}>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-meta">
          {BRAND_NAME} is operated by <strong>{LEGAL_ENTITY}</strong>. Version{' '}
          <span data-testid="legal-version">{POLICY_VERSION}</span> · effective{' '}
          {POLICY_VERSION}.
        </p>
        <div className="legal-body">{children}</div>
        <hr className="legal-rule" />
        <address className="legal-contact">
          Questions? Contact {LEGAL_ENTITY} at{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} data-testid="legal-contact-email">
            {LEGAL_CONTACT_EMAIL}
          </a>
          .
        </address>
      </main>
    </div>
  );
}
